import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material"
import MDBox from "../MDBox"
import { ReactComponent as CustomHomeIcon } from "components/icons/svgs/class.svg"
import { ReactComponent as CustomDateIcon } from "components/icons/svgs/calendar.svg"
import { ReactComponent as CustomDateCheckIcon } from "components/icons/svgs/calendar-check.svg"
import { ReactComponent as CustomEventIcon } from "components/icons/svgs/event.svg"
import { ReactComponent as CustomClockIcon } from "components/icons/svgs/clock.svg"
import { ReactComponent as CustomReportIcon } from "components/icons/svgs/report.svg"

const pad2 = (n) => String(n).padStart(2, "0")

// Input: "2026-01-02 16:00:00.000"
const parseApiDateTime = (s) => {
  if (!s) return null
  const iso = s.replace(" ", "T")
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? null : d
}

const formatDateDMY = (d) => {
  if (!d) return ""
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`
}

const formatTimeHM = (d) => {
  if (!d) return ""
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`
}

const toUiData = (apiList = []) => {
  const classesThatNeedCover = []
  const events = []
  const reports = []

  apiList.forEach((row) => {
    const ev = row?.event
    const data = ev?.data
    if (!ev || !data) return

    const start = parseApiDateTime(data.startTime)
    const end = parseApiDateTime(data.endTime)
    const deadline = parseApiDateTime(ev.deadline)

    const className = data.className || data.classCode || "-"
    const schedule =
      start && end ? `${formatTimeHM(start)} - ${formatTimeHM(end)}` : "-"
    if (ev.type === "Lesson") {
      classesThatNeedCover.push({
        className,
        date: start ? formatDateDMY(start) : "-",
        classSchedule: schedule,
      })
    }

    events.push({
      eventType: ev.type || "-",
      className,
      date: start ? formatDateDMY(start) : "-",
      eventTime: schedule,
    })

    if (deadline) {
      reports.push({
        className,
        reportType: ev.type || "Deadline",
        dueDate: `${formatTimeHM(deadline)} - ${formatDateDMY(deadline)}`,
      })
    }
  })

  return { classesThatNeedCover, events, reports }
}

const mapAwareInfoToUiData = (awareInfo) => {
  if (!awareInfo) {
    return { classesThatNeedCover: [], events: [], reports: [] }
  }
  const classesThatNeedCover = (awareInfo.classes || []).map((item) => ({
    className: item.className || "-",
    date: item.schedule ? item.schedule.split(" ")[0] : "-",
    classSchedule: item.schedule ? item.schedule.split(" ")[1] || "-" : "-",
  }))
  const events = (awareInfo.events || []).map((item) => ({
    eventType: item.type || "-",
    className: item.className || "-",
    date: item.date || "-",
    eventTime: item.time || "-",
  }))
  const reports = (awareInfo.reports || []).map((item) => ({
    className: item.className || "-",
    reportType: item.reportType || "Deadline",
    dueDate: item.dueDate || "-",
  }))
  return { classesThatNeedCover, events, reports }
}

const LeaveRelatedInfomationContent = ({
  uiData,
  dataRelatedInformation,
  awareInfo,
}) => {
  const resolvedUiData =
    uiData ||
    (awareInfo
      ? mapAwareInfoToUiData(awareInfo)
      : toUiData(
          Array.isArray(dataRelatedInformation) ? dataRelatedInformation : []
        ))

  return (
    <MDBox className="modal-leave-related-information-content">
      {/* Classes That Need Cover */}
      <Accordion
        className="accordion"
        defaultExpanded
        sx={{ border: "unset", boxShadow: "unset" }}
      >
        <AccordionSummary className="accordion-header">
          <Typography className="accordion-header-title" variant="h6">
            Classes that need cover
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, borderColor: "transparent" }}>
          <div className="accordion-list">
            {resolvedUiData.classesThatNeedCover.length === 0 ? (
              <Typography sx={{ p: 2 }} variant="body2">
                No data
              </Typography>
            ) : (
              resolvedUiData.classesThatNeedCover.map((item, index) => (
                <div key={index} className="accordion-list-item">
                  <MDBox className="accordion-list-item-content">
                    <Typography
                      className="accordion-list-item-content-line"
                      variant="body2"
                    >
                      <CustomHomeIcon /> Class Name: <b>{item.className}</b>
                    </Typography>
                    <Typography
                      className="accordion-list-item-content-line"
                      variant="body2"
                    >
                      <CustomDateIcon /> Date: <b>{item.date}</b>
                    </Typography>
                    <Typography
                      className="accordion-list-item-content-line"
                      variant="body2"
                    >
                      <CustomDateCheckIcon /> Schedule:{" "}
                      <b>{item.classSchedule}</b>
                    </Typography>
                  </MDBox>
                </div>
              ))
            )}
          </div>
        </AccordionDetails>
      </Accordion>

      <MDBox className="accordion-divider" />

      {/* Events */}
      <Accordion
        className="accordion"
        defaultExpanded
        sx={{ border: "unset", boxShadow: "unset" }}
      >
        <AccordionSummary className="accordion-header">
          <Typography className="accordion-header-title" variant="h6">
            Events
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, borderColor: "transparent" }}>
          <List className="accordion-list">
            {resolvedUiData.events.length === 0 ? (
              <Typography sx={{ p: 2 }} variant="body2">
                No data
              </Typography>
            ) : (
              resolvedUiData.events.map((item, index) => (
                <ListItem key={index} className="accordion-list-item">
                  <ListItemText
                    secondary={
                      <MDBox className="accordion-list-item-content">
                        <Typography
                          className="accordion-list-item-content-line"
                          variant="body2"
                        >
                          <CustomEventIcon /> Event type:{" "}
                          <b>{item.eventType}</b>
                        </Typography>
                        <Typography
                          className="accordion-list-item-content-line"
                          variant="body2"
                        >
                          <CustomHomeIcon /> Class Name: <b>{item.className}</b>
                        </Typography>
                        <Typography
                          className="accordion-list-item-content-line"
                          variant="body2"
                        >
                          <CustomDateIcon /> Date: <b>{item.date}</b>
                        </Typography>
                        <Typography
                          className="accordion-list-item-content-line"
                          variant="body2"
                        >
                          <CustomClockIcon /> Time: <b>{item.eventTime}</b>
                        </Typography>
                      </MDBox>
                    }
                  />
                </ListItem>
              ))
            )}
          </List>
        </AccordionDetails>
      </Accordion>

      <MDBox className="accordion-divider" />

      {/* Reports */}
      <Accordion
        className="accordion"
        defaultExpanded
        sx={{ border: "unset", boxShadow: "unset" }}
      >
        <AccordionSummary className="accordion-header">
          <Typography className="accordion-header-title" variant="h6">
            Reports
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, borderColor: "transparent" }}>
          <List className="accordion-list">
            {resolvedUiData.reports.length === 0 ? (
              <Typography sx={{ p: 2 }} variant="body2">
                No data
              </Typography>
            ) : (
              resolvedUiData.reports.map((item, index) => (
                <ListItem key={index} className="accordion-list-item">
                  <ListItemText
                    secondary={
                      <MDBox className="accordion-list-item-content">
                        <Typography
                          className="accordion-list-item-content-line"
                          variant="body2"
                        >
                          <CustomHomeIcon /> Class: <b>{item.className}</b>
                        </Typography>
                        <Typography
                          className="accordion-list-item-content-line"
                          variant="body2"
                        >
                          <CustomReportIcon /> Report Type:{" "}
                          <b>{item.reportType}</b>
                        </Typography>
                        <Typography
                          className="accordion-list-item-content-line"
                          variant="body2"
                        >
                          <CustomClockIcon /> Due Date: <b>{item.dueDate}</b>
                        </Typography>
                      </MDBox>
                    }
                  />
                </ListItem>
              ))
            )}
          </List>
        </AccordionDetails>
      </Accordion>
    </MDBox>
  )
}

export default LeaveRelatedInfomationContent
